import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ButtonPrimary } from "./Reveal";
import { PhotoUpload } from "./PhotoUpload";
import { useContent } from "../context/ContentContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const initialForm = {
  naam: "",
  email: "",
  telefoon: "",
  adres: "",
  postcode: "",
  plaats: "",
  dienst: "Timmerwerken",
  planning: "",
  budget: "",
  contact_voorkeur: "Telefoon",
  bericht: "",
};

const planningOptions = [
  "Zo snel mogelijk",
  "Binnen 1 tot 3 maanden",
  "Binnen 3 tot 6 maanden",
  "Later dit jaar of volgend jaar",
  "Nog geen idee, ik oriënteer me",
];

const budgetOptions = [
  "Tot € 2.500",
  "€ 2.500 tot € 7.500",
  "€ 7.500 tot € 15.000",
  "€ 15.000 tot € 30.000",
  "Meer dan € 30.000",
  "Weet ik nog niet",
];

const contactOptions = ["Telefoon", "WhatsApp", "E-mail"];

const inputClass =
  "w-full rounded-[4px] border border-[#D8D6D0] bg-white px-4 py-3 text-[15px] text-[#111111] placeholder:text-[#A0A0A0] outline-none transition-colors duration-200 focus:border-[#111111]";
const labelClass =
  "mb-1.5 block font-display text-xs font-bold uppercase tracking-[0.15em] text-[#555555]";

const SectionTitle = ({ step, children }) => (
  <div className="mb-5 flex items-center gap-3 border-b border-[#DDD9D0] pb-3">
    <span className="font-display text-xl font-bold text-[#B9B7B0]">{step}</span>
    <h3 className="font-display text-base font-bold uppercase tracking-wide text-[#111111]">{children}</h3>
  </div>
);

const Field = ({ id, label, required, children }) => (
  <div>
    <label htmlFor={id} className={labelClass}>
      {label}
      {required && " *"}
    </label>
    {children}
  </div>
);

export const QuoteForm = ({ onSent }) => {
  const { services } = useContent();
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/quote-requests`, { ...form, fotos: photos.map((p) => p.url) });
      toast.success("Aanvraag verzonden", {
        description: "Bedankt voor je aanvraag. Ik neem zo snel mogelijk contact met je op.",
      });
      onSent();
    } catch (err) {
      toast.error("Verzenden mislukt", {
        description: "Er ging iets mis. Probeer het opnieuw of bel me direct.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="contact-form" onSubmit={handleSubmit} className="border border-[#DDD9D0] bg-[#EFEDE8] p-6 sm:p-10">
      <SectionTitle step="01">Je gegevens</SectionTitle>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="naam" label="Naam" required>
          <input id="naam" data-testid="contact-form-name" type="text" required value={form.naam} onChange={update("naam")} placeholder="Je naam" className={inputClass} />
        </Field>
        <Field id="telefoon" label="Telefoonnummer">
          <input id="telefoon" data-testid="contact-form-phone" type="tel" value={form.telefoon} onChange={update("telefoon")} placeholder="06 - 12 34 56 78" className={inputClass} />
        </Field>
        <Field id="email" label="E-mailadres" required>
          <input id="email" data-testid="contact-form-email" type="email" required value={form.email} onChange={update("email")} placeholder="jij@voorbeeld.nl" className={inputClass} />
        </Field>
        <Field id="contact_voorkeur" label="Hoe wil je dat ik contact opneem?">
          <div className="flex gap-2" data-testid="contact-form-preference">
            {contactOptions.map((opt) => (
              <button
                key={opt}
                type="button"
                data-testid={`contact-form-preference-${opt.toLowerCase()}`}
                onClick={() => setForm((f) => ({ ...f, contact_voorkeur: opt }))}
                className={`flex-1 rounded-[4px] border px-3 py-3 font-display text-xs font-bold uppercase tracking-wide transition-colors duration-200 ${
                  form.contact_voorkeur === opt
                    ? "border-[#111111] bg-[#111111] text-white"
                    : "border-[#D8D6D0] bg-white text-[#555555] hover:border-[#111111]"
                }`}
              >
                {opt}
              </button>
            ))}
          </div>
        </Field>
        <Field id="adres" label="Straat en huisnummer">
          <input id="adres" data-testid="contact-form-address" type="text" value={form.adres} onChange={update("adres")} placeholder="Bijv. Dorpsstraat 12" className={inputClass} />
        </Field>
        <div className="grid grid-cols-5 gap-3">
          <div className="col-span-2">
            <Field id="postcode" label="Postcode">
              <input id="postcode" data-testid="contact-form-postcode" type="text" value={form.postcode} onChange={update("postcode")} placeholder="1716 AA" className={inputClass} />
            </Field>
          </div>
          <div className="col-span-3">
            <Field id="plaats" label="Plaats">
              <input id="plaats" data-testid="contact-form-city" type="text" value={form.plaats} onChange={update("plaats")} placeholder="Opmeer" className={inputClass} />
            </Field>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <SectionTitle step="02">Je project</SectionTitle>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="dienst" label="Type dienst">
          <select id="dienst" data-testid="contact-form-service-select" value={form.dienst} onChange={update("dienst")} className={`${inputClass} appearance-none`}>
            {services.map((s) => (
              <option key={s.slug} value={s.title}>{s.title}</option>
            ))}
            <option value="Anders">Anders / weet ik nog niet</option>
          </select>
        </Field>
        <Field id="planning" label="Wanneer wil je het laten uitvoeren?">
          <select id="planning" data-testid="contact-form-planning-select" value={form.planning} onChange={update("planning")} className={`${inputClass} appearance-none`}>
            <option value="">Maak een keuze</option>
            {planningOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </Field>
        <Field id="budget" label="Budgetindicatie (optioneel)">
          <select id="budget" data-testid="contact-form-budget-select" value={form.budget} onChange={update("budget")} className={`${inputClass} appearance-none`}>
            <option value="">Maak een keuze</option>
            {budgetOptions.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
          </select>
        </Field>
        <div className="sm:col-span-2">
          <Field id="bericht" label="Omschrijving van je project" required>
            <textarea
              id="bericht"
              data-testid="contact-form-message"
              required
              rows={6}
              value={form.bericht}
              onChange={update("bericht")}
              placeholder="Bijvoorbeeld: ik wil graag de kozijnen aan de voorzijde laten vervangen. Het gaat om 3 ramen en een voordeur, het huis is uit 1965..."
              className={`${inputClass} resize-none`}
            />
          </Field>
          <p className="mt-2 text-xs text-[#8E8D8A]">
            Handig om te vermelden: afmetingen (ongeveer), bouwjaar van de woning, huidige materialen en wat je graag anders wilt.
          </p>
        </div>
      </div>

      <div className="mt-10">
        <SectionTitle step="03">Foto's van de situatie</SectionTitle>
      </div>
      <PhotoUpload photos={photos} onChange={setPhotos} />

      <div className="mt-8 flex flex-wrap items-center gap-4">
        <ButtonPrimary type="submit" testid="contact-form-submit-button" disabled={sending}>
          {sending ? "Versturen..." : "Verstuur aanvraag"}
        </ButtonPrimary>
        <p className="text-sm text-[#555555]">Vrijblijvend. Ik reageer meestal binnen één werkdag.</p>
      </div>

      <p className="mt-5 text-xs leading-relaxed text-[#8E8D8A]">
        Je gegevens en foto's gebruik ik alleen om contact met je op te nemen over je aanvraag. Lees meer in de{" "}
        <Link to="/privacy" data-testid="contact-privacy-link" className="underline underline-offset-2 hover:text-[#111111]">
          privacyverklaring
        </Link>
        . Op offertes en opdrachten zijn mijn{" "}
        <Link to="/algemene-voorwaarden" data-testid="contact-voorwaarden-link" className="underline underline-offset-2 hover:text-[#111111]">
          algemene voorwaarden
        </Link>{" "}
        van toepassing.
      </p>
    </form>
  );
};
